/**
 * Broyari — Complete speelset
 * - <broy-bundle>: koopblok op de speelset-landingspagina (?view=speelset): voegt grondbox + speelmat in één keer toe,
 *   sticky mobiele CTA zolang de hoofdknop niet in beeld is.
 * - <broy-bundle-upsell>: compacte verwijzing naar de speelset op de grondbox-PDP; ververst bij variantwissel.
 * - [data-broy-cart-set]: in de winkelwagen de speelmat als set toevoegen.
 * Setkorting: de kortingscode (data-code) wordt ALLEEN toegepast bij een bewuste keuze voor de set
 * (via /cart/update.js, bestaande codes blijven staan). Los toevoegen = losse prijzen.
 * Opruimen: zodra de set niet meer in de winkelwagen zit (geen speelmat met _bundle of geen grondbox 120x180),
 * wordt de setcode weer uit de winkelwagen gehaald, zodat er geen korting achterblijft.
 * Geen tracking-code: toevoegingen via /cart/add.js worden door de bestaande Shopify-integraties gemeten.
 * Instellingen (korting, voorraadregel, varianten): snippets/broy-bundle.liquid.
 */
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const BUNDLE_ID = 'complete-speelset';
const SET_CODE = 'COMPLETE-SPEELSET';
const MAT_HANDLE = 'speelmat-baby-foam';
const GB_HANDLES = ['broyari-grondbox-baby-beige', 'grondbox-zwart'];
const SET_SIZE = '120*180cm';

function sectionIdList() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function addItems(items, sourceEl, code) {
  const sectionIds = sectionIdList();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const res = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items,
      sections: code ? undefined : sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });
  let data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');
  if (code) {
    // Setkorting alleen bij bewuste setkeuze: code toevoegen aan de bestaande codes.
    const current = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const codes = (current.discount_codes || []).map((d) => d.code).filter(Boolean);
    if (!codes.some((c) => c.toUpperCase() === code.toUpperCase())) codes.push(code);
    const up = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    data = await up.json();
    if (!up.ok || data.status) throw new Error(data.description || data.message || 'discount failed');
  }
  const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  if (data.sections && sectionIds.length) {
    sourceEl.dispatchEvent(
      new CartAddEvent(cart, 'broyari-bundle', {
        source: 'broyari-bundle',
        itemCount: cart.item_count,
        variantId: String(items[0].id),
        sections: data.sections,
      })
    );
  }
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    try { drawer.open(); } catch (e) {}
  }
  return cart;
}

class BroyBundle extends HTMLElement {
  connectedCallback() {
    this.cta = this.querySelector('.broy-bundle__cta');
    this.sticky = this.querySelector('[data-broy-bundle-sticky]');
    this.addEventListener('click', this.onClick);
    if (this.sticky && this.cta && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
      });
      this.io.observe(this.cta);
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  onClick = (e) => {
    const add = e.target.closest('[data-broy-bundle-add]');
    if (add && !add.disabled) this.add();
  };

  async add() {
    if (this.dataset.available !== 'true') return;
    const buttons = this.querySelectorAll('[data-broy-bundle-add]');
    if ([...buttons].some((b) => b.getAttribute('aria-busy') === 'true')) return;
    const err = this.querySelector('[data-broy-bundle-error]');
    const items = [
      { id: Number(this.dataset.setGb), quantity: 1, properties: { _bundle: BUNDLE_ID } },
      { id: Number(this.dataset.setMat), quantity: 1, properties: { _bundle: BUNDLE_ID } },
    ];
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    if (err) err.hidden = true;
    try {
      await addItems(items, this, this.dataset.code);
    } catch (error) {
      console.error('[broyari-bundle]', error);
      if (err) err.hidden = false;
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }
}

class BroyBundleUpsell extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.handleUpdate);
  }
  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleUpdate);
  }
  handleUpdate = (event) => {
    const fresh = event.detail?.data?.html?.querySelector?.('broy-bundle-upsell');
    if (fresh) morph(this, fresh, { childrenOnly: true });
  };
}

if (!customElements.get('broy-bundle')) customElements.define('broy-bundle', BroyBundle);
if (!customElements.get('broy-bundle-upsell')) customElements.define('broy-bundle-upsell', BroyBundleUpsell);

/* Winkelwagen: speelmat als Complete speelset toevoegen (bewuste setkeuze → setkorting) */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-broy-cart-set]') : null;
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;
  try {
    await addItems([{ id: Number(btn.dataset.variantId), quantity: 1, properties: { _bundle: BUNDLE_ID } }], btn, btn.dataset.code);
  } catch (error) {
    console.error('[broyari-bundle]', error);
    btn.disabled = false;
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

/* Galerij op de speelset-landingspagina: pijltjes + teller */
document.addEventListener('click', (e) => {
  const nav = e.target instanceof Element ? e.target.closest('[data-bs-nav]') : null;
  if (!nav) return;
  const track = nav.parentElement.querySelector('[data-bs-track]');
  if (track) track.scrollBy({ left: Number(nav.dataset.bsNav) * track.clientWidth, behavior: 'smooth' });
});
document.addEventListener(
  'scroll',
  (e) => {
    const track = e.target instanceof Element && e.target.matches('[data-bs-track]') ? e.target : null;
    if (!track) return;
    const count = track.parentElement.querySelector('[data-bs-count]');
    if (!count) return;
    const total = track.children.length;
    const idx = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)) + 1;
    count.textContent = `${Math.min(idx, total)} / ${total}`;
  },
  true
);

/* Setcode opruimen als de set uit de winkelwagen is gehaald */
let cleaning = false;
async function cleanupSetCode() {
  if (cleaning) return;
  cleaning = true;
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
    if (!codes.some((c) => c.toUpperCase() === SET_CODE)) return;
    const items = cart.items || [];
    const hasSetMat = items.some((i) => i.handle === MAT_HANDLE && i.properties && i.properties._bundle === BUNDLE_ID);
    const hasGb = items.some((i) => GB_HANDLES.includes(i.handle) && (i.variant_options || [])[0] === SET_SIZE);
    if (hasSetMat && hasGb) return;
    const sectionIds = sectionIdList();
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.filter((c) => c.toUpperCase() !== SET_CODE).join(','),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (data.sections && sectionIds.length) {
      document.dispatchEvent(
        new CartUpdateEvent(data, 'broyari-bundle-cleanup', { itemCount: data.item_count, sections: data.sections })
      );
    }
  } catch (e) {
    console.error('[broyari-bundle]', e);
  } finally {
    cleaning = false;
  }
}
document.addEventListener(ThemeEvents.cartUpdate, (e) => {
  if (e.detail && e.detail.sourceId === 'broyari-bundle-cleanup') return;
  setTimeout(cleanupSetCode, 300);
});
if (/\/cart\/?$/.test(window.location.pathname)) cleanupSetCode();
